import React, {ReactNode} from "react";
import {styled} from "@reside/ui";
import {RoleNameEnum} from "../../../../utils/types/common";
import {PermissionsManagementSearchField} from "./PermissionsManagementSearchField";
import {PermissionsManagementRoleFilter} from "./PermissionsManagementRoleFilter";
import {PermissionsManagementPermissionsFilter} from "./PermissionsManagementPermissionsFilter";
import {PermissionsManagementClearFiltersButton} from "./PermissionsManagementClearFiltersButton";
import {PermissionsManagementBulkActionsBar} from "./PermissionsManagementBulkActionsBar";
import {PermissionsManagementFilters} from "./permissionsManagementFilters.types";
import {
  clearPermissionsManagementFilters,
  hasActivePermissionsManagementFilters,
} from "./permissionsManagementFilters.helpers";

type Props = Readonly<{
  availableRoles: ReadonlyArray<RoleNameEnum>;
  filters: PermissionsManagementFilters;
  onFiltersChange: (filters: PermissionsManagementFilters) => void;
  selectedUserCount: number;
  isRemovingPermissions: boolean;
  onBulkEditPermissions: () => void;
  onRemoveAllPermissions: () => void;
  pagination?: ReactNode;
}>;

export const PermissionsManagementToolbar = ({
  availableRoles,
  filters,
  onFiltersChange,
  selectedUserCount,
  isRemovingPermissions,
  onBulkEditPermissions,
  onRemoveAllPermissions,
  pagination,
}: Props) => {
  const hasActiveFilters = hasActivePermissionsManagementFilters(filters);
  const hasSelection = selectedUserCount > 0;

  return (
    <Toolbar>
      <PermissionsManagementSearchField
        value={filters.searchTerm}
        onChange={searchTerm =>
          onFiltersChange({
            ...filters,
            searchTerm,
          })
        }
      />
      <ControlsRow>
        {hasSelection ? (
          <PermissionsManagementBulkActionsBar
            selectedCount={selectedUserCount}
            isRemovingPermissions={isRemovingPermissions}
            onBulkEditPermissions={onBulkEditPermissions}
            onRemoveAllPermissions={onRemoveAllPermissions}
          />
        ) : (
          <FiltersGroup>
            <PermissionsManagementRoleFilter
              availableRoles={availableRoles}
              selectedRoles={filters.roleFilters}
              onRolesChange={roleFilters =>
                onFiltersChange({
                  ...filters,
                  roleFilters,
                })
              }
            />
            <PermissionsManagementPermissionsFilter
              selectedFilters={filters.permissionsFilters}
              onFiltersChange={permissionsFilters =>
                onFiltersChange({
                  ...filters,
                  permissionsFilters,
                })
              }
            />
            {hasActiveFilters && (
              <PermissionsManagementClearFiltersButton
                onClick={() => onFiltersChange(clearPermissionsManagementFilters())}
              />
            )}
          </FiltersGroup>
        )}
        {pagination}
      </ControlsRow>
    </Toolbar>
  );
};

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`;

const ControlsRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const FiltersGroup = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;
